import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../../utils/createScopedI18n';
import { configNotariesI18n } from './i18n';

const normalizeStatus = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (normalized === 'inactive' || normalized === 'inactivo' || normalized === 'inactiva') return 'inactive';
  return 'active';
};

const EMAIL_PATTERN = /^[\w.+-]+@[\w-]+\.[\w.-]+$/;

const EMPTY_NOTARY = {
  id: null,
  name: '',
  comuna: '',
  region: '',
  address: '',
  phone: '',
  contactPerson: '',
  email: '',
  hasConvenio: false,
  notes: '',
  status: 'active',
};

export default function NotaryCreateEditModal({
  Modal,
  Input,
  Dropdown,
  show,
  mode = 'create',
  value,
  onChange,
  onClose,
  onSave,
}) {
  const { language } = useLanguage();
  const notariesLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configNotariesI18n });

  const isEdit = mode === 'edit';
  const [local, setLocal] = useState(EMPTY_NOTARY);

  useEffect(() => {
    configNotariesI18n.changeLanguage(notariesLanguage);
  }, [notariesLanguage]);

  useEffect(() => {
    if (show) {
      setLocal({
        id: value?.id ?? null,
        name: value?.name ?? '',
        comuna: value?.comuna ?? '',
        region: value?.region ?? '',
        address: value?.address ?? '',
        phone: value?.phone ?? '',
        contactPerson: value?.contactPerson ?? '',
        email: value?.email ?? '',
        hasConvenio: value?.hasConvenio ?? false,
        notes: value?.notes ?? '',
        status: normalizeStatus(value?.status),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, value?.id]);

  const nameValid = useMemo(() => local.name.trim().length > 0, [local.name]);
  const emailValid = useMemo(
    () => !local.email.trim() || EMAIL_PATTERN.test(local.email.trim()),
    [local.email]
  );
  const isValid = nameValid && emailValid && ['active', 'inactive'].includes(local.status);

  const setField = (key) => (eventOrValue) => {
    const valueToSet = eventOrValue?.target
      ? eventOrValue.target.value
      : eventOrValue;
    setLocal((prev) => ({ ...prev, [key]: valueToSet }));
  };

  const handleSave = () => {
    if (!isValid) return;
    onChange?.(local);
    onSave?.(local);
  };

  if (!Modal || !Input) return null;

  const convenioOptions = [
    { label: t('config_notaries_filter_convenio_yes'), value: true },
    { label: t('config_notaries_filter_convenio_no'), value: false },
  ];
  const convenioOptionFrom = (currentValue) =>
    convenioOptions.find((option) => option.value === Boolean(currentValue)) ||
    convenioOptions[1];

  const statusOptions = [
    { label: t('config_notaries_status_active'), value: 'active' },
    { label: t('config_notaries_status_inactive'), value: 'inactive' },
  ];
  const statusOptionFrom = (currentValue) =>
    statusOptions.find((option) => option.value === normalizeStatus(currentValue)) ||
    statusOptions[0];

  return (
    <Modal
      title={
        isEdit ? t('config_notaries_modal_title_edit') : t('config_notaries_modal_title_create')
      }
      show={show}
      onClose={onClose}
      primaryBtnTxt={
        isEdit ? t('config_notaries_modal_primary_edit') : t('config_notaries_modal_primary_create')
      }
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid}
      secondaryBtnTxt={t('config_notaries_cancel')}
      secondaryBtnAction={onClose}
      ctaAligment='right'
      width='720px'
    >
      <div className='row g-3'>
        <div className='col-12 col-md-8'>
          <Input
            label={t('config_notaries_modal_name_label')}
            required
            placeholder={t('config_notaries_modal_name_placeholder')}
            value={local.name}
            onChange={setField('name')}
            error={
              local.name && !nameValid
                ? t('config_notaries_modal_name_required')
                : ''
            }
          />
        </div>

        <div className='col-12 col-md-4'>
          <label className='form-label'>{t('config_notaries_modal_convenio_label')}</label>
          {Dropdown ? (
            <Dropdown
              options={convenioOptions}
              value={convenioOptionFrom(local.hasConvenio)}
              onChange={(option) =>
                setLocal((prev) => ({
                  ...prev,
                  hasConvenio: Boolean(option?.value),
                }))
              }
              placeholder={t('config_notaries_modal_convenio_placeholder')}
              width='100%'
              menuPortalTarget={document.body}
              menuPosition='fixed'
            />
          ) : (
            <select
              className='form-select'
              value={local.hasConvenio ? 'yes' : 'no'}
              onChange={(event) =>
                setLocal((prev) => ({
                  ...prev,
                  hasConvenio: event.target.value === 'yes',
                }))
              }
            >
              <option value='yes'>{t('config_notaries_filter_convenio_yes')}</option>
              <option value='no'>{t('config_notaries_filter_convenio_no')}</option>
            </select>
          )}
        </div>

        <div className='col-12 col-md-6'>
          <Input
            label={t('config_notaries_modal_comuna_label')}
            placeholder={t('config_notaries_modal_comuna_placeholder')}
            value={local.comuna}
            onChange={setField('comuna')}
          />
        </div>

        <div className='col-12 col-md-6'>
          <Input
            label={t('config_notaries_modal_region_label')}
            placeholder={t('config_notaries_modal_region_placeholder')}
            value={local.region}
            onChange={setField('region')}
          />
        </div>

        <div className='col-12'>
          <Input
            label={t('config_notaries_modal_address_label')}
            placeholder={t('config_notaries_modal_address_placeholder')}
            value={local.address}
            onChange={setField('address')}
          />
        </div>

        <div className='col-12 col-md-6'>
          <Input
            label={t('config_notaries_modal_email_label')}
            placeholder={t('config_notaries_modal_email_placeholder')}
            value={local.email}
            onChange={setField('email')}
            error={
              local.email && !emailValid
                ? t('config_notaries_modal_email_invalid')
                : ''
            }
          />
        </div>

        <div className='col-12 col-md-6'>
          <Input
            label={t('config_notaries_modal_phone_label')}
            placeholder={t('config_notaries_modal_phone_placeholder')}
            value={local.phone}
            onChange={setField('phone')}
          />
        </div>

        <div className='col-12 col-md-6'>
          <Input
            label={t('config_notaries_modal_contact_label')}
            placeholder={t('config_notaries_modal_contact_placeholder')}
            value={local.contactPerson}
            onChange={setField('contactPerson')}
          />
        </div>

        {isEdit && (
          <div className='col-12 col-md-6'>
            <label className='form-label'>{t('config_notaries_modal_status_label')}</label>
            {Dropdown ? (
              <Dropdown
                options={statusOptions}
                value={statusOptionFrom(local.status)}
                onChange={(option) =>
                  setLocal((prev) => ({
                    ...prev,
                    status: option?.value || 'active',
                  }))
                }
                placeholder={t('config_notaries_modal_status_placeholder')}
                width='100%'
                menuPortalTarget={document.body}
                menuPosition='fixed'
              />
            ) : (
              <select
                className='form-select'
                value={local.status}
                onChange={setField('status')}
              >
                <option value='active'>{t('config_notaries_status_active')}</option>
                <option value='inactive'>{t('config_notaries_status_inactive')}</option>
              </select>
            )}
          </div>
        )}

        <div className='col-12'>
          <Input
            label={t('config_notaries_modal_notes_label')}
            placeholder={t('config_notaries_modal_notes_placeholder')}
            value={local.notes}
            onChange={setField('notes')}
          />
        </div>
      </div>
    </Modal>
  );
}
